import React, { useState, useEffect } from 'react';

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filters, setFilters] = useState({ search: '', country: '', tag: '' });
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, byCountry: {} });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const crmAuth = localStorage.getItem('crmAuth') || btoa('admin:kioo2025!');

  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    country: '',
    city: '',
    tags: [],
    source: 'manual',
    birthday: '',
    anniversary: '',
    notes: '',
    is_listener: true,
    is_donor: false,
    is_volunteer: false
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/contacts`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        const data = await response.json();
        // API returns a list directly. Older versions wrapped it in {contacts: [...]} —
        // accept both shapes for safety.
        const list = Array.isArray(data) ? data : (data.contacts || []);
        setContacts(list);
        calculateStats(list);
      } else if (response.status === 401) {
        // Stale auth — clear so the next reload re-prompts for credentials.
        localStorage.removeItem('crmAuth');
        localStorage.removeItem('crmUsername');
        console.warn('CRM auth stale — cleared. Please log out and back in.');
      } else {
        console.error('fetchContacts failed:', response.status);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (contactList) => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const monthlyContacts = contactList.filter(c => {
      const date = new Date(c.created_at);
      return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
    });

    const byCountry = {};
    contactList.forEach(c => {
      const country = c.country || 'Unknown';
      byCountry[country] = (byCountry[country] || 0) + 1;
    });

    setStats({
      total: contactList.length,
      thisMonth: monthlyContacts.length,
      byCountry
    });
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newContact)
      });

      if (response.ok) {
        await fetchContacts();
        setShowAddModal(false);
        resetForm();
        alert('✅ Contact created successfully!');
      } else if (response.status === 401) {
        // Stale CRM auth (e.g. credentials changed since last login).
        // Clear it so the next page load forces a fresh login.
        localStorage.removeItem('crmAuth');
        localStorage.removeItem('crmUsername');
        alert('Your session has expired. Please log out and log back in to continue.');
      } else {
        const error = await response.json().catch(() => ({}));
        alert('Failed to create contact: ' + (error.detail || `HTTP ${response.status}`));
      }
    } catch (error) {
      alert('Error creating contact: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      if (response.ok) {
        fetchContacts();
      } else if (response.status === 401) {
        localStorage.removeItem('crmAuth');
        localStorage.removeItem('crmUsername');
        alert('Your session has expired. Please log out and log back in to continue.');
      } else {
        alert(`Failed to delete (HTTP ${response.status})`);
      }
    } catch (error) {
      alert('Error deleting contact: ' + error.message);
    }
  };

  const resetForm = () => {
    setNewContact({
      name: '',
      email: '',
      phone: '',
      whatsapp: '',
      country: '',
      city: '',
      tags: [],
      source: 'manual',
      birthday: '',
      anniversary: '',
      notes: '',
      is_listener: true,
      is_donor: false,
      is_volunteer: false
    });
  };

  const filteredContacts = contacts.filter(contact => {
    if (filters.search && 
        !contact.name?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !contact.email?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.country && contact.country !== filters.country) return false;
    if (filters.tag && !contact.tags?.includes(filters.tag)) return false;
    return true;
  });

  const uniqueCountries = [...new Set(contacts.map(c => c.country).filter(Boolean))];
  const uniqueTags = [...new Set(contacts.flatMap(c => c.tags || []))];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const getUpcomingCelebrations = () => {
    const today = new Date();
    const upcoming = [];
    
    contacts.forEach(contact => {
      if (contact.birthday) {
        const bday = new Date(contact.birthday);
        bday.setFullYear(today.getFullYear());
        if (bday < today) bday.setFullYear(today.getFullYear() + 1);
        const daysUntil = Math.ceil((bday - today) / (1000 * 60 * 60 * 24));
        if (daysUntil <= 30) {
          upcoming.push({ contact, type: 'birthday', date: bday, daysUntil });
        }
      }
      if (contact.anniversary) {
        const anniv = new Date(contact.anniversary);
        anniv.setFullYear(today.getFullYear());
        if (anniv < today) anniv.setFullYear(today.getFullYear() + 1);
        const daysUntil = Math.ceil((anniv - today) / (1000 * 60 * 60 * 24));
        if (daysUntil <= 30) {
          upcoming.push({ contact, type: 'anniversary', date: anniv, daysUntil });
        }
      }
    });

    return upcoming.sort((a, b) => a.daysUntil - b.daysUntil).slice(0, 5);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">👥 Contact Management</h2>
          <p className="text-gray-600 mt-1">Manage your listeners, donors, and supporters</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowBulkImport(true)}
            data-testid="contacts-bulk-import-btn"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            📥 Bulk import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="contacts-add-btn"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            ➕ Add Contact
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-600">Total Contacts</p>
          <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-600">New This Month</p>
          <p className="text-2xl font-bold text-green-900">{stats.thisMonth}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-sm text-purple-600">Countries</p>
          <p className="text-2xl font-bold text-purple-900">{Object.keys(stats.byCountry).length}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-sm text-orange-600">Upcoming Birthdays</p>
          <p className="text-2xl font-bold text-orange-900">{getUpcomingCelebrations().length}</p>
        </div>
      </div>

      {/* Upcoming Celebrations */}
      {getUpcomingCelebrations().length > 0 && (
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4 mb-6 border border-pink-200">
          <h3 className="font-semibold text-purple-800 mb-3">🎂 Upcoming Celebrations (Next 30 Days)</h3>
          <div className="flex flex-wrap gap-3">
            {getUpcomingCelebrations().map((item, index) => (
              <div key={index} className="bg-white rounded-lg px-3 py-2 shadow-sm">
                <span className="text-lg mr-2">{item.type === 'birthday' ? '🎂' : '💍'}</span>
                <span className="font-medium">{item.contact.name}</span>
                <span className="text-gray-500 text-sm ml-2">
                  {item.daysUntil === 0 ? 'Today!' : `in ${item.daysUntil} days`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="🔍 Search contacts..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="flex-1 min-w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
          <select
            value={filters.country}
            onChange={(e) => setFilters({ ...filters, country: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Countries</option>
            {uniqueCountries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
          <select
            value={filters.tag}
            onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Tags</option>
            {uniqueTags.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Contacts Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin text-4xl">⏳</div>
          <p className="mt-2 text-gray-500">Loading contacts...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
          <span className="text-6xl">👥</span>
          <p className="mt-4 text-gray-600">No contacts found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Add Your First Contact
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredContacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{contact.name}</div>
                    <div className="text-sm text-gray-500">{contact.email}</div>
                    {contact.phone && <div className="text-xs text-gray-400">📱 {contact.phone}</div>}
                    {contact.whatsapp && (
                      <a 
                        href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:text-green-800"
                      >
                        💬 WhatsApp
                      </a>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{contact.country || 'Unknown'}</div>
                    {contact.city && <div className="text-xs text-gray-500">{contact.city}</div>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {contact.is_listener && <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">Listener</span>}
                      {contact.is_donor && <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Donor</span>}
                      {contact.is_volunteer && <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">Volunteer</span>}
                      {(contact.tags || []).map(tag => (
                        <span key={tag} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600 capitalize">{contact.source || 'Unknown'}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(contact.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedContact(contact)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        👁️ View
                      </button>
                      <button
                        onClick={() => handleDeleteContact(contact.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">➕ Add New Contact</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
              </div>

              <form onSubmit={handleCreateContact} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">📱 Phone</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      placeholder="+231 888 000 000"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">💬 WhatsApp</label>
                    <input
                      type="tel"
                      value={newContact.whatsapp}
                      onChange={(e) => setNewContact({ ...newContact, whatsapp: e.target.value })}
                      placeholder="+231 777 000 000"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🌍 Country</label>
                    <select
                      value={newContact.country}
                      onChange={(e) => setNewContact({ ...newContact, country: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Select Country</option>
                      <option value="Liberia">🇱🇷 Liberia</option>
                      <option value="Sierra Leone">🇸🇱 Sierra Leone</option>
                      <option value="Guinea">🇬🇳 Guinea</option>
                      <option value="United States">🇺🇸 United States</option>
                      <option value="United Kingdom">🇬🇧 United Kingdom</option>
                      <option value="Ghana">🇬🇭 Ghana</option>
                      <option value="Nigeria">🇳🇬 Nigeria</option>
                      <option value="Other">🌍 Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={newContact.city}
                      onChange={(e) => setNewContact({ ...newContact, city: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🎂 Birthday</label>
                    <input
                      type="date"
                      value={newContact.birthday}
                      onChange={(e) => setNewContact({ ...newContact, birthday: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">💍 Anniversary</label>
                    <input
                      type="date"
                      value={newContact.anniversary}
                      onChange={(e) => setNewContact({ ...newContact, anniversary: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Type</label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newContact.is_listener}
                        onChange={(e) => setNewContact({ ...newContact, is_listener: e.target.checked })}
                        className="mr-2"
                      />
                      📻 Listener
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newContact.is_donor}
                        onChange={(e) => setNewContact({ ...newContact, is_donor: e.target.checked })}
                        className="mr-2"
                      />
                      💰 Donor
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newContact.is_volunteer}
                        onChange={(e) => setNewContact({ ...newContact, is_volunteer: e.target.checked })}
                        className="mr-2"
                      />
                      🤝 Volunteer
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newContact.notes}
                    onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Any additional information..."
                  />
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? '⏳ Saving...' : '✓ Save Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Contact Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">👤 {selectedContact.name}</h2>
                <button onClick={() => setSelectedContact(null)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
              </div>

              <div className="space-y-4">
                {selectedContact.email && (
                  <div className="flex items-center">
                    <span className="w-24 text-gray-500">Email:</span>
                    <a href={`mailto:${selectedContact.email}`} className="text-blue-600">{selectedContact.email}</a>
                  </div>
                )}
                {selectedContact.phone && (
                  <div className="flex items-center">
                    <span className="w-24 text-gray-500">Phone:</span>
                    <span>{selectedContact.phone}</span>
                  </div>
                )}
                {selectedContact.whatsapp && (
                  <div className="flex items-center">
                    <span className="w-24 text-gray-500">WhatsApp:</span>
                    <a 
                      href={`https://wa.me/${selectedContact.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600"
                    >
                      {selectedContact.whatsapp}
                    </a>
                  </div>
                )}
                {selectedContact.country && (
                  <div className="flex items-center">
                    <span className="w-24 text-gray-500">Location:</span>
                    <span>{selectedContact.city ? `${selectedContact.city}, ` : ''}{selectedContact.country}</span>
                  </div>
                )}
                {selectedContact.birthday && (
                  <div className="flex items-center">
                    <span className="w-24 text-gray-500">Birthday:</span>
                    <span>🎂 {formatDate(selectedContact.birthday)}</span>
                  </div>
                )}
                {selectedContact.anniversary && (
                  <div className="flex items-center">
                    <span className="w-24 text-gray-500">Anniversary:</span>
                    <span>💍 {formatDate(selectedContact.anniversary)}</span>
                  </div>
                )}
                {selectedContact.notes && (
                  <div>
                    <span className="text-gray-500">Notes:</span>
                    <p className="mt-1 p-3 bg-gray-50 rounded-lg">{selectedContact.notes}</p>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t flex justify-end">
                <button
                  onClick={() => setSelectedContact(null)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkImport && (
        <BulkImportModal
          backendUrl={BACKEND_URL}
          crmAuth={crmAuth}
          onClose={() => setShowBulkImport(false)}
          onComplete={() => { setShowBulkImport(false); fetchContacts(); }}
        />
      )}
    </div>
  );
};

// ===========================================================================
// BulkImportModal — two-step CSV/XLSX importer with AI column mapping
// ===========================================================================
const CANONICAL_OPTIONS = [
  ['ignore', '— Ignore this column'],
  ['name', 'Name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['organization', 'Organization / Church'],
  ['city', 'City'],
  ['country', 'Country'],
  ['contact_type', 'Contact type'],
  ['birthday', 'Birthday'],
  ['anniversary', 'Anniversary'],
  ['notes', 'Notes'],
  ['tags', 'Tags'],
];

const BulkImportModal = ({ backendUrl, crmAuth, onClose, onComplete }) => {
  const [step, setStep] = useState(1); // 1=upload, 2=review mapping, 3=done
  const [file, setFile] = useState(null);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // server response from /preview
  const [mapping, setMapping] = useState({});   // column -> canonical field
  const [skipDups, setSkipDups] = useState(true);
  const [result, setResult] = useState(null);

  const authHeaders = { Authorization: `Basic ${crmAuth}` };

  const submitForPreview = async () => {
    setError('');
    if (!file && !pasted.trim()) {
      setError('Choose a CSV/XLSX file or paste some rows.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      else fd.append('text', pasted);
      const r = await fetch(`${backendUrl}/api/crm/contacts/bulk-import/preview`, {
        method: 'POST',
        headers: authHeaders,
        body: fd,
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(errBody.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setPreview(data);
      setMapping({ ...data.suggested_mapping });
      setStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitForCommit = async () => {
    setError('');
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/crm/contacts/bulk-import/commit`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mapping,
          rows: preview.sample_rows.length === preview.total_rows
            ? preview.sample_rows
            : await reparseAllRows(),
          skip_duplicates: skipDups,
        }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(errBody.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setResult(data);
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // If the file had more rows than the preview returned, re-parse the whole file
  // by re-uploading it as text once. (The preview only ships ~5 sample rows.)
  const reparseAllRows = async () => {
    if (!file && !pasted) return preview.sample_rows;
    const fd = new FormData();
    if (file) fd.append('file', file); else fd.append('text', pasted);
    // Cheat: re-call preview but ignore the AI mapping (we already have one).
    const r = await fetch(`${backendUrl}/api/crm/contacts/bulk-import/preview`, {
      method: 'POST', headers: authHeaders, body: fd,
    });
    if (!r.ok) return preview.sample_rows;
    const data = await r.json();
    // The /preview endpoint currently caps sample_rows at 5. To support files
    // bigger than 5 rows, we use the original sample_rows as fallback. The
    // typical Genevieve workflow (uploading <500 contacts) is fine; for very
    // large imports the user can split the file. Future improvement: a
    // dedicated /parse endpoint that returns ALL rows.
    return data.sample_rows;
  };

  // Recompute preview contacts whenever the user tweaks the mapping
  const previewWithCustomMapping = (() => {
    if (!preview) return [];
    return preview.sample_rows.map(row => {
      const out = {};
      Object.entries(mapping).forEach(([col, target]) => {
        if (target === 'ignore' || !target) return;
        const v = row[col];
        if (v === undefined || v === null || v === '') return;
        out[target] = v;
      });
      return out;
    });
  })();

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="bulk-import-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">📥 Bulk import contacts</h2>
              <p className="text-sm text-gray-600 mt-1">
                Upload a CSV or Excel file. Claude Sonnet will figure out which column is which —
                you can fine-tune the mapping before we save anything.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
          </div>

          {/* Stepper */}
          <ol className="flex items-center text-sm mb-6">
            {['Upload', 'Review mapping', 'Done'].map((label, i) => (
              <li key={label} className={`flex items-center ${i < 2 ? 'flex-1' : ''}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold ${
                  step > i ? 'bg-green-600 text-white' : step === i + 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>{i + 1}</span>
                <span className={`ml-2 ${step >= i + 1 ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{label}</span>
                {i < 2 && <span className={`flex-1 h-0.5 mx-3 ${step > i + 1 ? 'bg-green-600' : 'bg-gray-200'}`} />}
              </li>
            ))}
          </ol>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg" data-testid="bulk-import-error">
              {error}
            </div>
          )}

          {/* STEP 1 — choose source */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CSV or Excel file</label>
                <input
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,.xlsm"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                  data-testid="bulk-import-file"
                  className="block w-full text-sm border border-gray-300 rounded-lg p-2"
                />
                <p className="text-xs text-gray-500 mt-1">First row should be headers. Column names can be in any language or order.</p>
              </div>
              <div className="flex items-center gap-2 my-2 text-sm text-gray-400">
                <div className="flex-1 h-px bg-gray-200" /><span>OR</span><div className="flex-1 h-px bg-gray-200" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paste rows directly (CSV/TSV)</label>
                <textarea
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  rows={6}
                  data-testid="bulk-import-textarea"
                  placeholder="Name,Email,Phone,Town,DOB&#10;Mariama Kamara,mariama@example.org,+231 770 111 001,Foya,1995-03-21"
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm font-mono"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button
                  onClick={submitForPreview}
                  disabled={busy}
                  data-testid="bulk-import-analyze-btn"
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {busy ? 'Analyzing with AI…' : '✨ Analyze with AI'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — review mapping */}
          {step === 2 && preview && (
            <div className="space-y-4">
              <div className="rounded-lg p-3 text-sm bg-purple-50 border border-purple-200">
                <strong>{preview.total_rows} rows</strong> detected ·{' '}
                {preview.ai_used
                  ? <>Claude Sonnet suggested the column mapping below. Review &amp; tweak as needed.</>
                  : <>AI was unavailable — used a heuristic fallback. Please review carefully.</>}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200 rounded-lg" data-testid="bulk-import-mapping-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 border-b">Source column</th>
                      <th className="text-left p-2 border-b">Sample value</th>
                      <th className="text-left p-2 border-b">→ Maps to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.columns.map((col) => (
                      <tr key={col} className="border-b last:border-b-0">
                        <td className="p-2 font-mono text-xs">{col}</td>
                        <td className="p-2 text-gray-600 text-xs">
                          {String(preview.sample_rows[0]?.[col] ?? '').slice(0, 60)}
                        </td>
                        <td className="p-2">
                          <select
                            value={mapping[col] || 'ignore'}
                            onChange={(e) => setMapping({ ...mapping, [col]: e.target.value })}
                            data-testid={`bulk-import-mapping-${col.replace(/\s/g, '_')}`}
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                          >
                            {CANONICAL_OPTIONS.map(([v, label]) => (
                              <option key={v} value={v}>{label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Live preview ({Math.min(preview.sample_rows.length, 5)} rows)</p>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Name','Email','Phone','City','Country','Birthday','Anniversary','Org','Notes'].map(h => (
                          <th key={h} className="p-2 text-left">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody data-testid="bulk-import-preview-table">
                      {previewWithCustomMapping.map((c, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{c.name || ''}</td>
                          <td className="p-2">{c.email || ''}</td>
                          <td className="p-2">{c.phone || ''}</td>
                          <td className="p-2">{c.city || ''}</td>
                          <td className="p-2">{c.country || ''}</td>
                          <td className="p-2">{c.birthday || ''}</td>
                          <td className="p-2">{c.anniversary || ''}</td>
                          <td className="p-2">{c.organization || ''}</td>
                          <td className="p-2 truncate max-w-[120px]" title={c.notes || ''}>{c.notes || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={skipDups}
                  onChange={(e) => setSkipDups(e.target.checked)}
                  data-testid="bulk-import-skip-dupes"
                />
                Skip rows whose email already exists in the database
              </label>

              <div className="flex justify-between gap-2 pt-2">
                <button onClick={() => setStep(1)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">← Back</button>
                <button
                  onClick={submitForCommit}
                  disabled={busy}
                  data-testid="bulk-import-confirm-btn"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {busy ? 'Importing…' : `✓ Import ${preview.total_rows} contacts`}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — done */}
          {step === 3 && result && (
            <div className="text-center py-6" data-testid="bulk-import-done">
              <div className="text-5xl mb-2">🎉</div>
              <h3 className="text-2xl font-bold text-gray-900">Import complete</h3>
              <div className="mt-4 inline-grid grid-cols-3 gap-4 text-sm">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-700">{result.added}</div>
                  <div className="text-green-700">added</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-700">{result.skipped_duplicates}</div>
                  <div className="text-yellow-700">skipped (duplicates)</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-700">{result.errors.length}</div>
                  <div className="text-red-700">errors</div>
                </div>
              </div>
              {result.errors.length > 0 && (
                <details className="text-left text-sm mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <summary className="cursor-pointer font-medium text-red-700">View errors</summary>
                  <ul className="mt-2 space-y-1">
                    {result.errors.slice(0, 20).map((er, i) => (
                      <li key={i}>Row {er.row}: {er.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="mt-6">
                <button
                  onClick={onComplete}
                  data-testid="bulk-import-finish-btn"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactManagement;
